import { useCallback, useState } from "react";
import StorageApiService from "../services/apiServices/StorageApiService";
import mediaUtility from "../services/utility/MediaUtility";
import {
  MediaFolder,
  MediaRecord,
  MediaUploadBatch,
  MediaUploadFailure,
  MediaUploadResult,
} from "../interfaces/MediaObject";

/** How many files upload concurrently. Each file still presigns just-in-time. */
const UPLOAD_CONCURRENCY = 3;

export interface UseMediaUploadOptions {
  /** Storage bucket folder for this feature (e.g. "business"). */
  folder: MediaFolder;
  /**
   * Persists the successfully-uploaded records (one batched call, owned by the
   * feature). May return the canonical records the backend stored — if it does,
   * those replace the local ones (e.g. to pick up server-normalized fields).
   */
  persist: (records: MediaRecord[]) => Promise<MediaRecord[] | void>;
}

export interface UseMediaUploadReturn {
  isUploading: boolean;
  /** Uploads files, then batch-persists the ones that uploaded. Never throws. */
  upload: (files: File[], section?: string) => Promise<MediaUploadBatch>;
}

function toMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Generic, feature-agnostic upload engine for the presigned-storage flow.
 *
 * Pipeline per file (isolated — one bad file never sinks the batch):
 *   validate → presign (StorageApiService.requestUpload)
 *            → PUT bytes (StorageApiService.uploadToTarget)
 * then a single batched `persist(successes)` for everything that uploaded.
 *
 * Returns a typed result per file (with the stage it failed at) so the UI can
 * report precisely and retry just the failures. Reused by the business gallery
 * now and product media later — only `folder` + `persist` differ.
 */
export function useMediaUpload({ folder, persist }: UseMediaUploadOptions): UseMediaUploadReturn {
  const [isUploading, setIsUploading] = useState(false);

  const uploadOne = useCallback(
    async (file: File, section?: string): Promise<MediaUploadResult> => {
      // 1. Validate
      const contentType = mediaUtility.resolveContentType(file);
      if (!contentType) {
        return {
          status: "error",
          file,
          stage: "validate",
          message: "This file type isn't supported.",
        };
      }

      // 2. Presign
      let target;
      try {
        target = await StorageApiService.requestUpload({
          fileName: file.name,
          folder,
          contentType,
        });
      } catch (err) {
        return {
          status: "error",
          file,
          stage: "presign",
          message: toMessage(err, "Couldn't start the upload. Please try again."),
        };
      }

      // 3. PUT bytes
      try {
        await StorageApiService.uploadToTarget(target, file, contentType);
      } catch (err) {
        return {
          status: "error",
          file,
          stage: "put",
          message: toMessage(err, "Couldn't upload this file. Please try again."),
        };
      }

      const record: MediaRecord = {
        mediaType: contentType,
        objectPath: target.objectPath,
        publicUrl: target.publicUrl,
        ...(section ? { section } : {}),
      };
      return { status: "success", file, record };
    },
    [folder]
  );

  const upload = useCallback(
    async (files: File[], section?: string): Promise<MediaUploadBatch> => {
      const empty: MediaUploadBatch = { results: [], succeeded: [], failed: [] };
      if (!files.length) return empty;

      setIsUploading(true);
      try {
        // Upload with bounded concurrency. uploadOne never throws, so a simple
        // worker pool over a shared cursor keeps at most N in flight.
        const results: MediaUploadResult[] = new Array(files.length);
        let cursor = 0;
        const worker = async () => {
          while (cursor < files.length) {
            const index = cursor++;
            results[index] = await uploadOne(files[index], section);
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, worker)
        );

        const uploaded = results.filter(
          (r): r is Extract<MediaUploadResult, { status: "success" }> => r.status === "success"
        );

        // 4. Batch-persist everything that uploaded. If persist fails, the bytes
        // are already in storage (harmless, unreferenced) — convert the uploaded
        // items to "persist"-stage failures so the UI can retry them.
        let succeeded: MediaRecord[] = [];
        if (uploaded.length) {
          try {
            const canonical = await persist(uploaded.map((r) => r.record));
            succeeded = Array.isArray(canonical) ? canonical : uploaded.map((r) => r.record);
          } catch (err) {
            const message = toMessage(
              err,
              "Uploaded, but couldn't save to the gallery. Please try again."
            );
            uploaded.forEach((r) => {
              const i = results.indexOf(r);
              if (i !== -1) {
                results[i] = { status: "error", file: r.file, stage: "persist", message };
              }
            });
          }
        }

        // Derive the final failure list after the persist step (it may have
        // demoted some uploaded items to "persist"-stage failures).
        const failed = results.filter(
          (r): r is MediaUploadFailure => r.status === "error"
        );
        return { results, succeeded, failed };
      } finally {
        setIsUploading(false);
      }
    },
    [uploadOne, persist]
  );

  return { isUploading, upload };
}
